import { useEffect, useRef, type ReactNode } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
    activityTitle: string; photoCount: number;
    /** Fara el, butonul „Trimite încă una" nu apare (provocarea pentru scoala: o singura depunere). */
    onAnother?: () => void;
    /** Titlul ecranului; implicit „Activitate trimisă". */
    titlu?: string;
    /** B3: slot sub „Trimite încă una" (profesorul: „Înapoi la activități"). Colaboratorul nu-l trece. */
    secondaryAction?: ReactNode;
    /**
     * B3: inlocuieste paragraful de sub card. Implicit, textul colaboratorului; profesorul are alt text
     * („Coordonatorul școlii" e chiar el, iar „poți închide pagina" n-are sens in aplicatie).
     */
    note?: ReactNode;
    /**
     * B3: muta focusul pe titlu la montare. Pe pagina profesorului butonul „Trimite" dispare cand apare
     * confirmarea, iar focusul ar cadea pe `body`, fara niciun anunt. Colaboratorul nu-l trece.
     */
    focusTitleOnMount?: boolean;
}

const NOTA_COLABORATOR = (
    <>
        Coordonatorul școlii și echipa ECOTIC primesc o notificare. Activitatea se punctează după aprobare.
        Depunerea e înregistrată — poți închide pagina oricând.
    </>
);

/**
 * Pasul 4 (UX §4.3): confirmare ca ecran (R12), nu toast. Fara buton „Am terminat" (F2.5, decizie
 * owner Task 14) - n-are unde sa duca, nu exista cont si nici home. Singura iesire ramane
 * inchiderea paginii, motiv pentru care textul o spune explicit.
 */
export function StepDone({ activityTitle, photoCount, onAnother, titlu, secondaryAction, note, focusTitleOnMount }: Props) {
    const titluRef = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        if (focusTitleOnMount) titluRef.current?.focus();
    }, [focusTitleOnMount]);

    return (
        <div className="flex flex-col items-center gap-4 pt-6 text-center">
            <CheckCircle2 className="size-16 text-magenta" aria-hidden />
            <h2
                ref={titluRef}
                tabIndex={focusTitleOnMount ? -1 : undefined}
                className="text-fluid-2xl font-semibold outline-none"
            >
                {titlu ?? 'Activitate trimisă'}
            </h2>
            <div className="w-full rounded-lg border border-hairline bg-white p-4 text-left">
                <p className="font-medium">{activityTitle}</p>
                <p className="text-sm text-dark/60 tabular-nums">{photoCount} {photoCount === 1 ? 'fișier' : 'fișiere'}</p>
                <p className="mt-2 flex items-center gap-1 text-sm"><Clock className="size-4" /> În aprobare</p>
            </div>
            <p className="text-fluid-base text-dark/70">
                {note ?? NOTA_COLABORATOR}
            </p>
            {onAnother && (
                <Button type="button" className="min-h-12 w-full" onClick={onAnother}>Trimite încă una</Button>
            )}
            {secondaryAction}
        </div>
    );
}
